import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, Users2, Trophy, Radio, UserPlus, Megaphone, ShieldCheck, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EXPORTS = [
  { key: "schedule-pdf",       label: "Schedule (PDF)",         icon: FileText,    endpoint: "/exports/schedule.pdf",           filename: "schedule.pdf",           roles: ["super_admin", "league_admin"], kind: "PDF" },
  { key: "draw-pdf",           label: "Tournament Draw (PDF)",  icon: FileText,    endpoint: "/exports/draw.pdf",               filename: "draw.pdf",               roles: ["super_admin", "league_admin"], kind: "PDF" },
  { key: "teams-csv",          label: "Teams (CSV)",            icon: Trophy,      endpoint: "/exports/teams.csv",              filename: "teams.csv",              roles: ["super_admin", "league_admin"] },
  { key: "teams-xlsx",         label: "Teams (Excel)",          icon: Trophy,      endpoint: "/exports/teams.xlsx",             filename: "teams.xlsx",             roles: ["super_admin", "league_admin"] },
  { key: "players-csv",        label: "Players (CSV)",          icon: Users2,      endpoint: "/exports/players.csv",            filename: "players.csv",            roles: ["super_admin", "league_admin"] },
  { key: "players-xlsx",       label: "Players (Excel)",        icon: Users2,      endpoint: "/exports/players.xlsx",           filename: "players.xlsx",           roles: ["super_admin", "league_admin"] },
  { key: "matches-csv",        label: "Matches (CSV)",          icon: Radio,       endpoint: "/exports/matches.csv",            filename: "matches.csv",            roles: ["super_admin", "league_admin"] },
  { key: "matches-xlsx",       label: "Matches (Excel)",        icon: Radio,       endpoint: "/exports/matches.xlsx",           filename: "matches.xlsx",           roles: ["super_admin", "league_admin"] },
  { key: "registrations-csv",  label: "Registrations (CSV)",    icon: UserPlus,    endpoint: "/exports/registrations.csv",      filename: "registrations.csv",      roles: ["super_admin", "league_admin"] },
  { key: "registrations-xlsx", label: "Registrations (Excel)",  icon: UserPlus,    endpoint: "/exports/registrations.xlsx",     filename: "registrations.xlsx",     roles: ["super_admin", "league_admin"] },
  { key: "sponsor-inquiries",  label: "Sponsor Leads (CSV)",    icon: Megaphone,   endpoint: "/exports/sponsor-inquiries.csv",  filename: "sponsor_inquiries.csv",  roles: ["super_admin", "league_admin"] },
  { key: "posters-zip",        label: "Player Posters (ZIP)",   icon: Users2,      endpoint: "/exports/posters.zip",            filename: "mara-posters-all.zip",   roles: ["super_admin", "league_admin"], kind: "ZIP" },
  { key: "users",              label: "Users (CSV)",            icon: ShieldCheck, endpoint: "/exports/users.csv",              filename: "users.csv",              roles: ["super_admin"] },
];

export default function Exports() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [posterPreset, setPosterPreset] = useState("default");
  const [samplePlayerId, setSamplePlayerId] = useState(null);

  const base = useMemo(
    () => (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, ""),
    []
  );

  // Pick the first available player (with a photo if possible) as the preset preview subject.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/players?limit=25");
        const list = Array.isArray(data) ? data : data?.items || [];
        if (!list.length) return;
        const withPhoto = list.find((p) => p.photo_path || p.photo_url) || list[0];
        if (alive) setSamplePlayerId(withPhoto.id);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  const download = async (it) => {
    setBusy(it.key);
    try {
      let endpoint = it.endpoint;
      if (it.key === "posters-zip") {
        endpoint = `${it.endpoint}?preset=${posterPreset}`;
      }
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = it.key === "posters-zip" ? `mara-posters-${posterPreset}.zip` : it.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
    setBusy(null);
  };

  const visible = EXPORTS.filter((e) => e.roles.includes(user?.role));

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Reporting</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white" data-testid="exports-title">CSV Exports</h1>
        <div className="text-xs text-zinc-400 mt-1">Download structured data for spreadsheets, accounting, or external systems.</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exports-grid">
        {visible.map((it) => {
          const Icon = it.icon;
          return (
            <Card key={it.key} className="p-5 bg-[#11241b] border-white/10 flex flex-col" data-testid={`export-card-${it.key}`}>
              <Icon className="w-7 h-7 text-[#00A86B] mb-2" />
              <div className="font-anton text-2xl uppercase tracking-tight text-white">{it.label}</div>
              <div className="text-xs text-zinc-500 mt-1">{it.filename}</div>
              {it.key === "posters-zip" && (
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Style Preset · tap to preview</label>
                  <div className="mt-2 grid grid-cols-4 gap-2" data-testid="poster-preset-picker">
                    {["default", "playoffs", "champion", "mvp"].map((p) => {
                      const active = posterPreset === p;
                      const thumb = samplePlayerId
                        ? `${base}/api/players/${samplePlayerId}/poster.png?preset=${p}`
                        : null;
                      return (
                        <button
                          key={p}
                          onClick={() => setPosterPreset(p)}
                          data-testid={`poster-preset-${p}`}
                          className={`group relative rounded-md overflow-hidden border transition-all ${
                            active
                              ? "border-[#F4B400] ring-2 ring-[#F4B400]/40"
                              : "border-white/10 hover:border-[#F4B400]/40 opacity-70 hover:opacity-100"
                          }`}
                          title={`Preview ${p}`}
                          aria-pressed={active}
                        >
                          <div className="aspect-[4/5] bg-black/60 relative">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={`${p} preset preview`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                                data-testid={`poster-preset-thumb-${p}`}
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-[9px] text-zinc-600">…</div>
                            )}
                          </div>
                          <div className={`text-[9px] uppercase tracking-widest py-1 text-center font-bold ${
                            active ? "bg-[#F4B400] text-black" : "bg-black/70 text-zinc-300 group-hover:text-white"
                          }`}>{p}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] text-zinc-500">
                    Active: <span className="text-[#F4B400] font-semibold">{posterPreset.toUpperCase()}</span>
                    {samplePlayerId && (
                      <>
                        {" · "}
                        <a
                          href={`${base}/api/players/${samplePlayerId}/poster.png?preset=${posterPreset}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-[#F4B400]"
                          data-testid="poster-preset-full-preview-link"
                        >Open full preview</a>
                      </>
                    )}
                  </div>
                </div>
              )}
              <Button onClick={() => download(it)} disabled={busy === it.key} className="mt-4 bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest" data-testid={`export-btn-${it.key}`}>
                <FileDown className="w-4 h-4 mr-2" /> {busy === it.key ? "Downloading…" : `Download ${it.kind || "CSV"}`}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
